import { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Table } from '@carboledger/ui';
import { buildTableLabels } from '@/config/tableLabels';
import { useTableState, useTranslations } from '@/hooks';
import { EMPTY_LIST } from '@/lib/utils';
import emptyWalletIllustration from '@/assets/illustrations/empty-wallet.svg';
import { useGetAccessUsersQuery, useGetActivityLogsQuery } from '../api/adminApi';
import { USER_OPTIONS_PAGE_SIZE } from '../constants/admin.constants';
import { buildActivityFilterDefinitions } from '../constants/adminFilters';
import { buildActivityLogColumns } from '../constants/activityLogColumns';
import { buildActivityUserOptions } from '../mappers/adminMappers';
import styles from './ActivityLogTable.module.scss';

const getActivityRowId = (row) => row.id;
const USER_OPTIONS_ARGS = Object.freeze({ page: 1, pageSize: USER_OPTIONS_PAGE_SIZE });

export const ActivityLogTable = () => {
  const t = useTranslations();
  const navigate = useNavigate();
  const table = useTableState();

  const { data, isFetching } = useGetActivityLogsQuery(table.queryParams);
  const rows = data?.rows ?? EMPTY_LIST;

  // `logged_by.id` is never selected server-side, so the User filter reads the users list.
  const { data: users } = useGetAccessUsersQuery(USER_OPTIONS_ARGS);
  const userOptions = useMemo(() => buildActivityUserOptions(users), [users]);

  const handleOpenAction = useCallback((row) => navigate(row.actionTo), [navigate]);

  const columns = useMemo(
    () =>
      buildActivityLogColumns(t, {
        serialOffset: (table.page - 1) * table.pageSize,
        onOpenAction: handleOpenAction,
      }),
    [t, table.page, table.pageSize, handleOpenAction],
  );

  const filterDefinitions = useMemo(
    () => buildActivityFilterDefinitions(t, userOptions),
    [t, userOptions],
  );
  const tableLabels = useMemo(() => buildTableLabels(t), [t]);

  const emptyState = useMemo(
    () => ({
      image: (
        <span className={styles.illustration}>
          <img src={emptyWalletIllustration} alt="" className={styles.illustrationArt} />
        </span>
      ),
      title: t('ADMIN_ACTIVITY_EMPTY_TITLE'),
      description: t('ADMIN_ACTIVITY_EMPTY_DESCRIPTION'),
    }),
    [t],
  );

  const noResults = useMemo(
    () => ({
      title: t('ADMIN_ACTIVITY_NO_RESULTS_TITLE'),
      description: t('COMMON_NO_RESULTS_DESCRIPTION'),
    }),
    [t],
  );

  // <Table> reports sort as { id, dir }; useTableState stores { field, direction }.
  const handleSortChange = useCallback(
    (next) => {
      table.setSort(next ? { field: next.id, direction: next.dir } : null);
      table.setPage(1);
    },
    [table],
  );

  const handleSearchChange = useCallback(
    (value) => {
      table.setSearch(value);
      table.setPage(1);
    },
    [table],
  );

  const handleFilterChange = useCallback(
    (values) => {
      table.setFilters(values);
      table.setPage(1);
    },
    [table],
  );

  return (
    <Table
      columns={columns}
      data={rows}
      total={data?.totalCount ?? 0}
      loading={isFetching}
      getRowId={getActivityRowId}
      searchable
      onSortChange={handleSortChange}
      onSearchChange={handleSearchChange}
      filters={filterDefinitions}
      onFilterChange={handleFilterChange}
      onPageChange={table.setPage}
      onPageSizeChange={table.setPageSize}
      emptyState={emptyState}
      noResults={noResults}
      labels={tableLabels}
    />
  );
};
